import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { useEffect } from 'react';
import { Toaster } from '@/components/ui/sonner';
import '@/App.css';

import { AuthProvider } from '@/lib/auth';
import { CartProvider } from '@/lib/cart';
import { FavoritesProvider } from '@/lib/favorites';
import { WishlistProvider } from '@/lib/wishlist';
import { FollowsProvider } from '@/lib/follows';
import { CompareProvider } from '@/lib/compare';
import { CompanyProvider } from '@/lib/company';
import { ProtectedRoute } from '@/components/ProtectedRoute';
import { CompareBar } from '@/components/CompareBar';

import Landing from '@/pages/Landing';
import Catalog from '@/pages/Catalog';
import ProductDetail from '@/pages/ProductDetail';
import Cart from '@/pages/Cart';
import Login from '@/pages/Login';
import Register from '@/pages/Register';
import Messages from '@/pages/Messages';
import PaymentResult from '@/pages/PaymentResult';
import AppAuthCallback from '@/pages/AppAuthCallback';
import EliminarCuenta from '@/pages/EliminarCuenta';
import EliminarCuentaConfirmar from '@/pages/EliminarCuentaConfirmar';
import AdminAccountDeletion from '@/pages/AdminAccountDeletion';
import AdminOrderDetail from '@/pages/AdminOrderDetail';
import AdminUserDetail from '@/pages/AdminUserDetail';
import { initNativeDeepLinks } from '@/lib/native';

initNativeDeepLinks();
import AuthCallback from '@/pages/AuthCallback';
import BuyerOrders from '@/pages/BuyerOrders';
import BuyerShippingRefunds from '@/pages/BuyerShippingRefunds';
import BuyerDashboard from '@/pages/BuyerDashboard';
import SellerOnboarding from '@/pages/SellerOnboarding';
import SellerDashboard from '@/pages/SellerDashboard';
import SellerProducts from '@/pages/SellerProducts';
import SellerOrders from '@/pages/SellerOrders';
import SellerSubscription from '@/pages/SellerSubscription';
import SellerShipping from '@/pages/SellerShipping';
import SellerWallet from '@/pages/SellerWallet';
import AdminDashboard from '@/pages/AdminDashboard';
import AdminUsers from '@/pages/AdminUsers';
import AdminSellers from '@/pages/AdminSellers';
import AdminSettings from '@/pages/AdminSettings';
import AdminPQR from '@/pages/AdminPQR';
import AdminOrders from '@/pages/AdminOrders';
import AdminWithdrawals from '@/pages/AdminWithdrawals';
import AdminFinance from '@/pages/AdminFinance';
import AdminLogistics from '@/pages/AdminLogistics';
import AdminPayouts from '@/pages/AdminPayouts';
import AdminPayoutsRuns from '@/pages/AdminPayoutsRuns';
import AdminIntegrations from '@/pages/AdminIntegrations';
import AdminDian from '@/pages/AdminDian';
import Notifications from '@/pages/Notifications';
import Favorites from '@/pages/Favorites';
import Compare from '@/pages/Compare';
import SellerProfile from '@/pages/SellerProfile';
import Wishlist from '@/pages/Wishlist';
import Follows from '@/pages/Follows';
import Profile from '@/pages/Profile';
import Ayuda from '@/pages/Ayuda';
import PQR from '@/pages/PQR';
import MyPQR from '@/pages/MyPQR';
import Ofertas from '@/pages/Ofertas';
import Envios from '@/pages/Envios';
import Descargar from '@/pages/Descargar';
import Garantias from '@/pages/Garantias';
import Novedades from '@/pages/Novedades';
import AdminAnnouncements from '@/pages/AdminAnnouncements';
import AdminLegalDocs from '@/pages/AdminLegalDocs';
import AdminShippingPromotions from '@/pages/AdminShippingPromotions';
import AdminShippingRefunds from '@/pages/AdminShippingRefunds';
import AdminShipments from '@/pages/AdminShipments';
import LogisticsPortal from '@/pages/LogisticsPortal';
import AdminFreeShippingReserve from '@/pages/AdminFreeShippingReserve';
import AdminPromotions from '@/pages/AdminPromotions';
import Promociones from '@/pages/Promociones';
import LegalStub from '@/pages/LegalStub';
import LegalDocument from '@/pages/LegalDocument';
import QuienesSomos from '@/pages/QuienesSomos';
import Contacto from '@/pages/Contacto';
import ForgotPassword from '@/pages/ForgotPassword';
import ResetPassword from '@/pages/ResetPassword';
import NotFound from '@/pages/NotFound';

function AppRouter() {
  const location = useLocation();
  // Global scroll-to-top on route change: cualquier navegación interna
  // (footer, header, botones, Link) inicia la nueva página en scroll 0,0.
  // No aplica al hash de OAuth callback (que se procesa abajo antes de render).
  const isOAuthHash = !!location.hash?.includes('session_id=');
  useEffect(() => {
    if (isOAuthHash) return;
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
  }, [location.pathname, location.search, isOAuthHash]);
  // Process OAuth callback synchronously during render (avoids race conditions)
  if (location.hash?.includes('session_id=') && location.pathname !== '/auth/app-callback') return <AuthCallback />;

  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/promociones" element={<Promociones />} />
      <Route path="/catalogo" element={<Catalog />} />
      <Route path="/producto/:id" element={<ProductDetail />} />
      <Route path="/carrito" element={<Cart />} />
      <Route path="/pago/resultado" element={<PaymentResult />} />
      <Route path="/mensajes" element={<Messages />} />
      <Route path="/vendedor/mensajes" element={<Messages />} />
      <Route path="/login" element={<Login />} />
      <Route path="/registro" element={<Register />} />
      <Route path="/olvide-contrasena" element={<ForgotPassword />} />
      <Route path="/restablecer-contrasena" element={<ResetPassword />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/auth/app-callback" element={<AppAuthCallback />} />
      <Route path="/eliminar-cuenta" element={<EliminarCuenta />} />
      <Route path="/eliminar-cuenta/confirmar" element={<EliminarCuentaConfirmar />} />
      <Route path="/comparar" element={<Compare />} />
      <Route path="/tienda/:id" element={<SellerProfile />} />
      <Route path="/ayuda" element={<Ayuda />} />
      <Route path="/pqr" element={<PQR />} />
      <Route path="/mis-pqr" element={
        <ProtectedRoute><MyPQR /></ProtectedRoute>
      } />
      <Route path="/ofertas" element={<Ofertas />} />
      <Route path="/envios" element={<Envios />} />
      <Route path="/descargar" element={<Descargar />} />
      <Route path="/garantias" element={<Garantias />} />
      <Route path="/novedades" element={<Novedades />} />
      <Route path="/quienes-somos" element={<QuienesSomos />} />
      <Route path="/terminos" element={<LegalDocument slug="terms" canonical="/terminos" />} />
      <Route path="/privacidad" element={<LegalDocument slug="privacy" canonical="/privacidad" />} />
      <Route path="/politicas" element={<LegalDocument slug="purchase" canonical="/politicas" />} />
      <Route path="/devoluciones" element={<LegalDocument slug="returns" canonical="/devoluciones" />} />
      <Route path="/politica-de-envios" element={<LegalDocument slug="shipping" canonical="/politica-de-envios" />} />
      <Route path="/contacto" element={<Contacto />} />

      <Route path="/mis-reembolsos-envio" element={
        <ProtectedRoute roles={['buyer']}><BuyerShippingRefunds /></ProtectedRoute>
      } />
      <Route path="/mis-pedidos" element={
        <ProtectedRoute roles={['buyer']}><BuyerOrders /></ProtectedRoute>
      } />
      <Route path="/comprador" element={
        <ProtectedRoute roles={['buyer']}><BuyerDashboard /></ProtectedRoute>
      } />
      <Route path="/notificaciones" element={
        <ProtectedRoute><Notifications /></ProtectedRoute>
      } />
      <Route path="/favoritos" element={
        <ProtectedRoute roles={['buyer']}><Favorites /></ProtectedRoute>
      } />
      <Route path="/lista-de-deseos" element={
        <ProtectedRoute roles={['buyer']}><Wishlist /></ProtectedRoute>
      } />
      <Route path="/vendedores-que-sigo" element={
        <ProtectedRoute roles={['buyer']}><Follows /></ProtectedRoute>
      } />
      <Route path="/perfil" element={
        <ProtectedRoute><Profile /></ProtectedRoute>
      } />

      <Route path="/vendedor" element={
        <ProtectedRoute roles={['seller']}><SellerDashboard /></ProtectedRoute>
      } />
      <Route path="/vendedor/onboarding" element={
        <ProtectedRoute roles={['seller']}><SellerOnboarding /></ProtectedRoute>
      } />
      <Route path="/vendedor/productos" element={
        <ProtectedRoute roles={['seller']}><SellerProducts /></ProtectedRoute>
      } />
      <Route path="/vendedor/pedidos" element={
        <ProtectedRoute roles={['seller']}><SellerOrders /></ProtectedRoute>
      } />
      <Route path="/vendedor/envios" element={
        <ProtectedRoute roles={['seller']}><SellerShipping /></ProtectedRoute>
      } />
      <Route path="/vendedor/suscripcion" element={
        <ProtectedRoute roles={['seller']}><SellerSubscription /></ProtectedRoute>
      } />
      <Route path="/vendedor/wallet" element={
        <ProtectedRoute roles={['seller']}><SellerWallet /></ProtectedRoute>
      } />
      <Route path="/vendedor/finanzas" element={
        <ProtectedRoute roles={['seller']}><SellerWallet /></ProtectedRoute>
      } />

      <Route path="/admin" element={
        <ProtectedRoute roles={['admin']}><AdminDashboard /></ProtectedRoute>
      } />
      <Route path="/admin/usuarios/:userId" element={
        <ProtectedRoute roles={['admin']}><AdminUserDetail /></ProtectedRoute>
      } />
      <Route path="/admin/usuarios" element={
        <ProtectedRoute roles={['admin']}><AdminUsers /></ProtectedRoute>
      } />
      <Route path="/admin/vendedores" element={
        <ProtectedRoute roles={['admin']}><AdminSellers /></ProtectedRoute>
      } />
      <Route path="/admin/pedidos/:orderId" element={
        <ProtectedRoute roles={['admin']}><AdminOrderDetail /></ProtectedRoute>
      } />
      <Route path="/admin/pedidos" element={
        <ProtectedRoute roles={['admin']}><AdminOrders /></ProtectedRoute>
      } />
      <Route path="/admin/retiros" element={
        <ProtectedRoute roles={['admin']}><AdminWithdrawals /></ProtectedRoute>
      } />
      <Route path="/admin/finanzas" element={
        <ProtectedRoute roles={['admin']}><AdminFinance /></ProtectedRoute>
      } />
      <Route path="/admin/payouts" element={
        <ProtectedRoute roles={['admin']}><AdminPayouts /></ProtectedRoute>
      } />
      <Route path="/admin/payouts/runs" element={
        <ProtectedRoute roles={['admin']}><AdminPayoutsRuns /></ProtectedRoute>
      } />
      <Route path="/logistica" element={
        <ProtectedRoute roles={['logistics']}><LogisticsPortal /></ProtectedRoute>
      } />
      <Route path="/admin/configuracion/envios/envio-gratis" element={
        <ProtectedRoute roles={['admin']}><AdminFreeShippingReserve /></ProtectedRoute>
      } />
      <Route path="/admin/configuracion/envios/despachos" element={
        <ProtectedRoute roles={['admin']}><AdminShipments /></ProtectedRoute>
      } />
      <Route path="/admin/configuracion/envios/reembolsos" element={
        <ProtectedRoute roles={['admin']}><AdminShippingRefunds /></ProtectedRoute>
      } />
      <Route path="/admin/configuracion/envios/promociones" element={
        <ProtectedRoute roles={['admin']}><AdminShippingPromotions /></ProtectedRoute>
      } />
      <Route path="/admin/configuracion/envios" element={
        <ProtectedRoute roles={['admin']}><AdminLogistics /></ProtectedRoute>
      } />
      <Route path="/admin/configuracion" element={
        <ProtectedRoute roles={['admin']}><AdminSettings /></ProtectedRoute>
      } />
      <Route path="/admin/pqr" element={
        <ProtectedRoute roles={['admin']}><AdminPQR /></ProtectedRoute>
      } />
      <Route path="/admin/eliminacion-cuentas" element={
        <ProtectedRoute roles={['admin']}><AdminAccountDeletion /></ProtectedRoute>
      } />
      <Route path="/admin/novedades" element={
        <ProtectedRoute roles={['admin']}><AdminAnnouncements /></ProtectedRoute>
      } />
      <Route path="/admin/documentos-legales" element={
        <ProtectedRoute roles={['admin']}><AdminLegalDocs /></ProtectedRoute>
      } />
      <Route path="/admin/promociones" element={
        <ProtectedRoute roles={['admin']}><AdminPromotions /></ProtectedRoute>
      } />
      <Route path="/admin/facturacion-dian" element={
        <ProtectedRoute roles={['admin']}><AdminDian /></ProtectedRoute>
      } />
      <Route path="/admin/integraciones" element={
        <ProtectedRoute roles={['admin']}><AdminIntegrations /></ProtectedRoute>
      } />

      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}

export default function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <CompanyProvider>
          <AuthProvider>
            <CartProvider>
              <FavoritesProvider>
                <WishlistProvider>
                  <FollowsProvider>
                    <CompareProvider>
                      <AppRouter />
                      <CompareBar />
                      <Toaster position="top-right" richColors />
                    </CompareProvider>
                  </FollowsProvider>
                </WishlistProvider>
              </FavoritesProvider>
            </CartProvider>
          </AuthProvider>
        </CompanyProvider>
      </BrowserRouter>
    </div>
  );
}
